// ABOUTME: The plate's breathing line: the round's countdown beside an IN/OUT cue that cross-fades in one fixed slot.
// ABOUTME: A baseline row on desktop, stacked and centred on a phone; the screen supplies the seconds left and which cue is showing.

import { Box, Typography } from '@mui/material';
import { CROSSING_HEADLINE_INK } from '../../../../theme/crossingTokens';
import { breathClock } from '../../crossing/breathMath';
import { useFrameScale } from '../../crossing/useFrameScale';

// The plate's authored frame width, fed to the shared S22 scale calc only for its phone/desktop switch.
const PLATE_FRAME_WIDTH = 360;
const CUE_SLOT_WIDTH = 260;
const CUE_SLOT_HEIGHT = 14;
const CROSSFADE = '0.3s ease';
// The incoming line waits for the outgoing one to fade, so the two never overlap on screen.
const INCOMING_DELAY = '0.32s';

interface BreathPlateLineProps {
  leftSec: number; // whole seconds left, 0 once the round is done
  cueIn: boolean; // true while the IN line is the one showing
  reducedMotion: boolean;
}

export default function BreathPlateLine({ leftSec, cueIn, reducedMotion }: BreathPlateLineProps) {
  const { mob } = useFrameScale(PLATE_FRAME_WIDTH);

  const cueLine = (testId: string, text: string, visible: boolean) => (
    <Box
      aria-hidden="true"
      data-testid={testId}
      sx={{
        position: 'absolute', top: 0, left: 0, right: 0, whiteSpace: 'nowrap', textAlign: mob ? 'center' : 'left',
        fontSize: '11px', fontWeight: 800, letterSpacing: '1.6px', lineHeight: `${CUE_SLOT_HEIGHT}px`, color: CROSSING_HEADLINE_INK,
      }}
      style={{
        opacity: visible ? 1 : 0,
        transition: reducedMotion ? 'none' : `opacity ${CROSSFADE} ${visible ? INCOMING_DELAY : '0s'}`,
      }}
    >
      {text}
    </Box>
  );

  return (
    <Box
      data-testid="breath-plate-line"
      data-layout={mob ? 'column' : 'row'}
      sx={{
        display: 'flex', flexDirection: mob ? 'column' : 'row', alignItems: mob ? 'center' : 'baseline', gap: mob ? '6px' : '10px',
        my: '6px',
      }}
    >
      {/* No per-second animation: the countdown just changes. Not a live region, so it is not read out every second. */}
      <Typography
        component="div"
        role="timer"
        sx={{ fontFamily: 'Newsreader, serif', fontSize: '27px', fontWeight: 600, lineHeight: 1, color: CROSSING_HEADLINE_INK }}
      >
        {breathClock(leftSec)}
      </Typography>
      <Box sx={{ position: 'relative', display: 'inline-block', width: CUE_SLOT_WIDTH, height: CUE_SLOT_HEIGHT, flexShrink: 0 }}>
        {cueLine('breath-cue-in', 'BREATHE IN WITH THE SWELL', cueIn)}
        {cueLine('breath-cue-out', 'BREATHE OUT WITH THE WIND', !cueIn)}
      </Box>
    </Box>
  );
}
